import React from 'react';
import {
  Eye, ClipboardList, Layers, Settings2, LineChart, ShieldCheck,
  Smartphone, Monitor, BarChart3, FileText, ChevronRight,
} from 'lucide-react';
import { useReveal } from '../../hooks/useReveal';
import RevealItem from '../RevealItem';
import { pastel } from '../../lib/palette';

const steps = [
  { n: '01', icon: Eye, title: 'Observar', text: 'Detección de señales tempranas en el día a día escolar.', tone: pastel.coral },
  { n: '02', icon: ClipboardList, title: 'Registrar', text: 'Texto, voz o evidencia fotográfica desde donde estés.', tone: pastel.violet },
  { n: '03', icon: Layers, title: 'Clasificar', text: 'Priorización preventiva orientada a la acción.', tone: pastel.amber },
  { n: '04', icon: Settings2, title: 'Gestionar', text: 'Acciones, derivaciones y seguimiento por responsable.', tone: pastel.pink },
  { n: '05', icon: LineChart, title: 'Analizar', text: 'Tendencias y prioridades visibles para el equipo directivo.', tone: pastel.sky },
  { n: '06', icon: ShieldCheck, title: 'Prevenir', text: 'Decisiones oportunas y mejora continua del clima escolar.', tone: pastel.mint },
];

const modules = [
  { icon: Smartphone, title: 'App móvil', text: 'Registro ágil desde cualquier lugar del colegio.', tone: pastel.violet },
  { icon: Monitor, title: 'Panel web', text: 'Gestión centralizada y segura para el equipo de convivencia.', tone: pastel.sky },
  { icon: BarChart3, title: 'Dashboard directivo', text: 'Visión estratégica para mejor toma de decisiones.', tone: pastel.teal },
  { icon: FileText, title: 'Reportes institucionales', text: 'Información clara para rendición de cuentas y planificación.', tone: pastel.amber },
];

const ComoFunciona = () => {
  const head = useReveal();
  const modHead = useReveal();
  return (
    <section id="como-funciona" data-testid="como-funciona-section" className="relative py-14 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div ref={head.ref} className="reveal max-w-3xl">
          <div className="chip" data-testid="como-chip">Cómo funciona PreventiApp</div>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-deep tracking-tight leading-tight">
            Un ecosistema preventivo para{' '}
            <span className="text-brand-violet">observar, registrar y gestionar</span> la convivencia escolar.
          </h2>
        </div>

        {/* 6 pasos — secuencia con flechas, cada uno con tono pastel propio */}
        <ol className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 relative" data-testid="como-steps">
          {steps.map((s, idx) => (
            <RevealItem
              key={s.n}
              as="li"
              delay={idx * 60}
              className="relative group rounded-2xl bg-white p-7 border transition-all hover:-translate-y-0.5"
              style={{ borderColor: s.tone.bg }}
              data-testid={`step-${s.title.toLowerCase()}`}
            >
              {/* Borde superior pastel */}
              <span aria-hidden className="absolute inset-x-6 -top-px h-[3px] rounded-full" style={{ background: s.tone.fg, opacity: 0.7 }} />

              <div className="flex items-center justify-between">
                <span
                  className="text-[11px] font-extrabold tracking-[0.22em] px-2.5 py-1 rounded-md"
                  style={{ background: s.tone.bg, color: s.tone.fg }}
                >
                  PASO {s.n}
                </span>
                <span
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ background: s.tone.bg, color: s.tone.fg }}
                >
                  <s.icon className="w-6 h-6" />
                </span>
              </div>
              <h3 className="mt-5 text-xl font-bold text-brand-deep">{s.title}</h3>
              <p className="mt-2 text-base text-brand-muted leading-relaxed">{s.text}</p>

              {/* Chevron entre pasos en desktop (excepto el último de cada fila) */}
              {idx < steps.length - 1 && (
                <ChevronRight
                  aria-hidden
                  className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 w-7 h-7 text-brand-violet/30 group-hover:text-brand-violet/60 transition-colors"
                  style={{ display: (idx + 1) % 3 === 0 ? 'none' : undefined }}
                />
              )}
            </RevealItem>
          ))}
        </ol>

        {/* 4 módulos soporte — DISEÑO DIFERENCIADO: bandas oscuras horizontales con device frame */}
        <div ref={modHead.ref} className="reveal mt-20">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <div className="chip" data-testid="modules-chip">Puntos de acceso</div>
              <h3 className="mt-4 text-2xl sm:text-3xl font-bold text-brand-deep tracking-tight">
                Una sola plataforma, <span className="text-brand-violet">varios puntos de acceso</span>.
              </h3>
              <p className="mt-2 text-base text-brand-muted max-w-2xl">
                Registro en terreno, gestión en oficina y visión estratégica para dirección — todo conectado.
              </p>
            </div>
          </div>

          {/* Layout horizontal alterno (zig-zag): row con preview a la izquierda en pares, derecha en impares */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {modules.map((m, idx) => (
              <RevealItem
                key={m.title}
                delay={idx * 70}
                className="relative rounded-2xl border border-brand bg-white overflow-hidden flex"
                data-testid={`module-${m.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {/* Banda lateral pastel */}
                <div
                  className="w-2 shrink-0"
                  style={{ background: `linear-gradient(180deg, ${m.tone.fg} 0%, ${m.tone.bg} 100%)` }}
                  aria-hidden
                />
                <div className="flex-1 flex items-center gap-5 p-5 sm:p-6">
                  <span
                    className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ background: m.tone.bg, color: m.tone.fg }}
                  >
                    <m.icon className="w-6 h-6" />
                  </span>
                  <div className="min-w-0">
                    <h4 className="text-base sm:text-lg font-bold text-brand-deep">{m.title}</h4>
                    <p className="mt-1 text-sm text-brand-muted leading-relaxed">{m.text}</p>
                  </div>
                </div>
              </RevealItem>
            ))}
          </div>

          <div className="mt-10 rounded-2xl border border-brand bg-brand-light p-5 sm:p-6" data-testid="como-uso-movil">
            <p className="text-sm sm:text-base text-brand-deep leading-relaxed">
              <strong>Registro rápido desde dispositivos institucionales</strong> para equipos
              de convivencia, inspectoría, docentes y asistentes autorizados.
            </p>
          </div>
        </div>

        {/* Pie banner */}
        <div className="mt-10 rounded-3xl border border-brand bg-brand-light p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-testid="como-banner">
          <span className="w-12 h-12 rounded-2xl bg-white text-brand-violet flex items-center justify-center border border-brand">
            <ShieldCheck className="w-6 h-6" />
          </span>
          <p className="text-base sm:text-lg text-brand-deep font-semibold">
            Trazabilidad total: historial, evidencia, responsables y resultados.
          </p>
        </div>

        {/* Nota Ley 21.801 — franja seria y prudente */}
        <div className="mt-6 rounded-2xl border border-brand/50 bg-white/80 backdrop-blur px-5 py-4 sm:px-6" data-testid="como-nota-ley">
          <p className="text-xs sm:text-sm text-brand-muted leading-relaxed">
            Uso compatible con dispositivos institucionales y orientado a fortalecer la
            trazabilidad preventiva de la convivencia escolar en el nuevo contexto normativo
            de la <strong className="text-brand-deep">Ley 21.801</strong>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ComoFunciona;
